import React, { useState, useRef, useEffect } from "react";
import { Grid, Card, Typography, Box, Avatar, CircularProgress, IconButton } from "@mui/material";
import { styled } from "@mui/system";
import MDBox from "components/MDBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ChatbotMessageInput from "./ChatbotMessageInput";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import { useAuth0 } from "@auth0/auth0-react";
import StoreSelector from "components/StoreSelector";

const ChatbotContainer = styled(Box)(({ theme }) => ({
  height: "500px",
  overflowY: "auto",
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
}));

const MessageWrapper = styled(Box)(({ theme, sender }) => ({
  display: "flex",
  flexDirection: sender === "USER" ? "row-reverse" : "row",
  alignItems: "flex-start",
  marginBottom: theme.spacing(2),
}));

const MessageBubble = styled(Box)(({ theme, sender }) => ({
  backgroundColor: sender === "USER" ? theme.palette.primary.main : theme.palette.grey[100],
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1.5),
  maxWidth: "70%",
  boxShadow: theme.shadows[1],
}));

const MessageText = styled(Typography)(({ theme, sender }) => ({
  color: sender === "USER" ? theme.palette.primary.contrastText : theme.palette.text.primary,
}));

const AvatarWrapper = styled(Box)(({ theme }) => ({
  marginRight: theme.spacing(1),
  marginLeft: theme.spacing(1),
}));

const TypingIndicator = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(2),
}));

const Chatbot = () => {
  const { user } = useAuth0();
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const chatbotContainerRef = useRef(null);
  const [selectedStore, setSelectedStore] = useState("");
  const stores = [
    { id: "store1", name: "Store 1" },
    { id: "store2", name: "Store 2" },
    // Add more stores as needed
  ];

  useEffect(() => {
    fetchChatHistory();
  }, [selectedStore]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleStoreChange = (storeId) => {
    setSelectedStore(storeId);
  };

  const fetchChatHistory = async () => {
    try {
      const response = await fetch(
        `https://veclogic.com/backend/get-chat-history?userId=${user.sub}&page=1&limit=10&storeId=${selectedStore}`
      );
      const data = await response.json();
      if (data.success) {
        const sortedMessages = data.data.sort((a, b) => {
          if (a.message === "I'm your Chatahoot Virtual Buddy, What can I help you with?") {
            return -1; // Always place the special message at the top
          }
          if (b.message === "I'm your Chatahoot Virtual Buddy, What can I help you with?") {
            return 1;
          }
          return new Date(a.createdAt) - new Date(b.createdAt);
        });
        const formattedMessages = sortedMessages.map((message) => ({
          text: message.message,
          sender: message.senderType,
        }));
        setMessages(formattedMessages);
      }
    } catch (error) {
      console.error("Error fetching chat history:", error);
    }
  };

  const scrollToBottom = () => {
    chatbotContainerRef.current.scrollTop = chatbotContainerRef.current.scrollHeight;
  };

  const handleSendMessage = async (message) => {
    try {
      setMessages((prevMessages) => [...prevMessages, { text: message, sender: "USER" }]);
      setIsTyping(true);

      const response = await fetch("https://veclogic.com/backend/chat", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userId: user.sub,
          category: "BUY",
          subCategory: "VECLOGIC",
          message,
          storeId: selectedStore,
          authId: user.sub,
        }),
      });

      const data = await response.json();
      if (data.success) {
        setIsTyping(false);
        setMessages((prevMessages) => [
          ...prevMessages,
          { text: data.data.message, sender: "BOT" },
        ]);
      }
    } catch (error) {
      console.error("Error sending message:", error);
      setIsTyping(false);
    }
  };

  const handleClearChat = async () => {
    try {
      const response = await fetch(
        `https://veclogic.com/backend/clear-chat-history?userId=${user.sub}&storeId=${selectedStore}`,
        {
          method: "DELETE",
        }
      );
      const data = await response.json();
      if (data.success) {
        setMessages([]);
      }
    } catch (error) {
      console.error("Error clearing chat history:", error);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <MDBox
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="h6">Chatbot</Typography>
                  <IconButton
                    color="primary"
                    onClick={handleClearChat}
                    sx={{
                      backgroundColor: "action.hover",
                      "&:hover": {
                        backgroundColor: "action.selected",
                      },
                    }}
                  >
                    <ClearAllIcon />
                  </IconButton>
                </MDBox>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <StoreSelector
                      stores={stores}
                      selectedStore={selectedStore}
                      onStoreChange={handleStoreChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ChatbotContainer ref={chatbotContainerRef}>
                      {messages.map((message, index) => (
                        <MessageWrapper key={index} sender={message.sender}>
                          <AvatarWrapper>
                            <Avatar
                              alt={message.sender === "USER" ? "User" : "AI Agent"}
                              src={
                                message.sender === "USER" ? "/user-avatar.png" : "/ai-avatar.png"
                              }
                            />
                          </AvatarWrapper>
                          <MessageBubble sender={message.sender}>
                            <MessageText
                              variant="body1"
                              sender={message.sender}
                              dangerouslySetInnerHTML={{ __html: message.text }}
                            />
                          </MessageBubble>
                        </MessageWrapper>
                      ))}
                      {isTyping && (
                        <TypingIndicator>
                          <CircularProgress size={20} color="primary" />
                          <Typography variant="body2" style={{ marginLeft: "8px" }}>
                            AI Agent is typing...
                          </Typography>
                        </TypingIndicator>
                      )}
                    </ChatbotContainer>
                    <ChatbotMessageInput onSendMessage={handleSendMessage} />
                  </Grid>
                </Grid>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
};

export default Chatbot;
